// AiPanel — right-side AI chat panel (hideable, pushes main content).
// Uses the default active AI model from /ai/chat.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";

type Msg = { role: "user" | "assistant"; content: string };

const PANEL_W = 340;

const SYSTEM_INTRO: Msg = {
  role: "assistant",
  content:
    "안녕하세요! TIMPLE ERP AI 어시스턴트입니다 🤖\n현재 페이지의 데이터 조회, 분석, 수정 방법 등을 도와드릴 수 있어요. 무엇이든 물어보세요!",
};

export function AiPanel({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<Msg[]>([SYSTEM_INTRO]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [modelName, setModelName] = useState<string | null>(null);
  const loc = useLocation();
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const userMsg: Msg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const payload = {
        messages: [...messages.filter((m) => m !== SYSTEM_INTRO), userMsg],
        context: {
          path: loc.pathname,
          search: loc.search,
        },
      };
      const r = await api.post<{ content: string; model_name: string }>("/ai/chat", payload);
      setModelName(r.data.model_name);
      setMessages((prev) => [...prev, { role: "assistant", content: r.data.content }]);
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "AI 오류가 발생했습니다";
      toast.error("AI 오류", msg);
      setMessages((prev) => prev.slice(0, -1)); // remove the user message so they can retry
      setInput(text);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clear = () => setMessages([SYSTEM_INTRO]);

  return (
    <div
      style={{
        width: PANEL_W,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid var(--border)",
        background: "var(--bg)",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-2)",
          flexShrink: 0,
        }}
      >
        <I.Robot size={18} style={{ color: "var(--p-500)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>AI 어시스턴트</div>
          {modelName && (
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>{modelName}</div>
          )}
        </div>
        <button
          className="t-iconbtn"
          title="대화 초기화"
          onClick={clear}
          style={{ opacity: 0.6 }}
        >
          <I.Trash size={15} />
        </button>
        <button className="t-iconbtn" title="패널 닫기" onClick={onClose}>
          <I.PanelR size={16} />
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              flexDirection: m.role === "user" ? "row-reverse" : "row",
              gap: 8,
              alignItems: "flex-start",
            }}
          >
            {m.role === "assistant" && (
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "var(--p-500)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                <I.Robot size={14} style={{ color: "#fff" }} />
              </div>
            )}
            <div
              style={{
                maxWidth: "82%",
                padding: "8px 12px",
                borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                background: m.role === "user" ? "var(--p-500)" : "var(--bg-2)",
                color: m.role === "user" ? "#fff" : "var(--text-1)",
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <div
              style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "var(--p-500)", display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <I.Robot size={14} style={{ color: "#fff" }} />
            </div>
            <div
              style={{
                padding: "8px 12px", borderRadius: "14px 14px 14px 4px",
                background: "var(--bg-2)", color: "var(--text-3)",
                fontSize: 13,
              }}
            >
              <span style={{ animation: "pulse 1s infinite" }}>생각 중…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick prompts */}
      <div style={{ padding: "6px 14px 0", display: "flex", gap: 6, flexWrap: "wrap", flexShrink: 0 }}>
        {["현재 페이지 요약", "데이터 분석", "도움말"].map((q) => (
          <button
            key={q}
            onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
            style={{
              padding: "3px 10px",
              borderRadius: 12,
              border: "1px solid var(--border)",
              background: "var(--bg-2)",
              color: "var(--text-2)",
              fontSize: 11,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <div
        style={{
          padding: "8px 14px 12px",
          flexShrink: 0,
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="메시지 입력… (Enter: 전송, Shift+Enter: 줄바꿈)"
          rows={2}
          disabled={sending}
          style={{
            flex: 1,
            resize: "none",
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--bg-2)",
            color: "var(--text-1)",
            fontSize: 13,
            lineHeight: 1.5,
            outline: "none",
            fontFamily: "inherit",
          }}
        />
        <button
          onClick={send}
          disabled={!input.trim() || sending}
          className="t-btn solid"
          style={{ padding: "8px 12px", flexShrink: 0 }}
        >
          <I.Send size={16} />
        </button>
      </div>
    </div>
  );
}
